import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { X, Clock, CheckCircle, AlertTriangle, DollarSign, Calendar, Edit, Save, Printer, Calculator } from 'lucide-react';
import { calculateCheckoutImpact } from '../../utils/checkoutUtils';
import { PrintReceiptModal } from '../receipts/PrintReceiptModal';
import { useTranslation } from 'react-i18next';
import { useApi } from '../Store/ApiContext';
import { enqueueSnackbar } from 'notistack';

export const CheckoutRequestModal = ({
  isOpen,
  onClose,
  request,
  bookingPolicies,
  withConfirm = () => { }
}) => {
  const { t } = useTranslation();
  const { getData, responseApi, quickEditTable } = useApi();

  const [requestedCheckOut, setRequestedCheckOut] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customRefundAmount, setCustomRefundAmount] = useState(null);
  const [isEditingRefund, setIsEditingRefund] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [refundForPrint, setRefundForPrint] = useState(null);
  const [policy, setPolicy] = useState({});
  const [policyLoading, setPolicyLoading] = useState(false);

  const loadCheckoutPolicy = async () => {
    try {
      setPolicyLoading(true);
      const response = await getData(null, 'checkout/policy');
      if (response?.policy) {
        setPolicy(response.policy);
      }
    } catch (error) {
      console.error('Error loading checkout policy:', error);
      // Keep default policy if API fails
    } finally {
      setPolicyLoading(false);
    }
  };

  useEffect(() => {
    loadCheckoutPolicy();
  }, []);

  if (!isOpen || !request) return null; // guard if request not provided

  // Calculate nights using schema fields (reqFrom / reqTo)
  // const calculateNights = () => {
  //   if (!request?.reqFrom || !request?.reqTo) return 0;
  //   const checkIn = new Date(request.reqFrom);
  //   const checkOut = new Date(request.reqTo);
  //   return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  // };

  const nights = request.reqNights;
  const ratePerNight = nights > 0 ? (request.reqTotalCost || 0) / nights : 0;

  // use reqTo as original checkout date
  const checkoutDetails = requestedCheckOut
    ? calculateCheckoutImpact(request.reqTo, requestedCheckOut, policy, ratePerNight, bookingPolicies?.checkOutPolicy)
    : null;

  // Calculate final refund amount (use custom if set, otherwise policy calculation)
  const finalRefundAmount = checkoutDetails?.type === 'early'
    ? (customRefundAmount !== null ? customRefundAmount : Math.abs(checkoutDetails.additionalFees))
    : 0;

  // Calculate final fee amount for late checkout
  const finalFeeAmount = checkoutDetails?.type === 'late'
    ? (customRefundAmount !== null ? customRefundAmount : checkoutDetails.additionalFees)
    : 0;

  const handleSubmit = async () => {
    if (!requestedCheckOut || !checkoutDetails) return;

    setIsSubmitting(true);

    console.log(finalRefundAmount, finalFeeAmount);

    try {
      // Build payload expected by the API
      const payload = {
        originalRequestId: request._id || request.id,
        requestedCheckOut: moment.utc(requestedCheckOut).toISOString(),
        reason: reason.trim() || undefined,
        customAmount: customRefundAmount ? Math.ceil(Number(customRefundAmount)) : null,
        calculatedFinalAmount: finalRefundAmount ? Math.ceil(Number(finalRefundAmount)) : Math.ceil(Number(finalFeeAmount)),
      };

      // Call backend API
      const response = await responseApi('post', payload, 'checkout/requests');

      if (response?.message === 'success') {
        enqueueSnackbar(t('requestSubmitted'), { variant: 'success' });
        // Optionally show print modal or other flows here
        // to update data in parent list
        withConfirm(response.checkoutRequest.request._id, response.checkoutRequest._id);
        onClose();
      } else {
        enqueueSnackbar(<>{t('requestError')}<br />{response?.message}</>, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting checkout request:', error);
      enqueueSnackbar(<>{t('requestError')}<br />{error.message}</>, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRefund = () => {
    if (checkoutDetails?.type === 'early') {
      setCustomRefundAmount(Math.abs(checkoutDetails.additionalFees).toFixed(2));
      setIsEditingRefund(true);
    } else if (checkoutDetails?.type === 'late') {
      setCustomRefundAmount(checkoutDetails.additionalFees.toFixed(2));
      setIsEditingRefund(true);
    }
  };

  const handleSaveRefund = () => {
    setIsEditingRefund(false);
  };

  const handleCancelRefund = () => {
    setCustomRefundAmount(null);
    setIsEditingRefund(false);
  };

  const handlePrintRefundReceipt = () => {
    if (!checkoutDetails || checkoutDetails.type !== 'early' || finalRefundAmount <= 0) return;

    // Create a refund object for printing
    const refund = {
      id: `early-checkout-refund-${request._id || request.id || Date.now()}`,
      amountPaid: finalRefundAmount,
      receipt: null,
      receiptPreview: '',
      date: moment().format('YYYY-MM-DD'),
      notes: `Early checkout refund - ${Math.abs(checkoutDetails.hoursDifference).toFixed(1)} hours early${reason ? ` | ${reason}` : ''}`,
      status: 'confirmed'
    };
    console.log(request);
    request.deposits = request.reqPayments;

    setRefundForPrint(refund);
    setShowPrintModal(true);
  };

  // Convert formData to BookingRequest for printing
  const getBookingRequestForPrint = () => {
    return {
      reqCode: request.reqCode,
      clientName: request.reqClientName,
      phone: request.client.clientPhone || request.clientPhone,
      checkIn: request.reqFrom,
      checkOut: request.reqTo,
      unitName: request.unit?.unitName || '',
      unitCode: request.unit?.unitCode || '',
      guests: parseInt(request.reqGuests) || 0,
      totalAmount: request?.reqTotalCost || 0,
      deposits: request.reqPayments,
      refunds: request.reqPayments,
      status: request.reqStatus,
      createdAt: new Date().toISOString()
    };
  };

  const handlePrintLateCheckoutReceipt = () => {
    if (!checkoutDetails || checkoutDetails.type !== 'late' || Math.abs(finalFeeAmount) <= 0) return;

    // For late checkout, we print a fee receipt (not a refund)
    setShowPrintModal(true);
  };

  const handleAmountEdit = () => {
    if (checkoutDetails?.type === 'late') {
      setCustomRefundAmount(checkoutDetails.additionalFees.toFixed(2));
      setIsEditingRefund(true);
    }
  };


  const isEditingAmount = isEditingRefund;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{t('manageCheckout')}</h2>
                <p className="text-sm text-slate-500 mt-1">{request.reqClientName} - {request.unit?.unitName} {request.unit?.unitCode}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                aria-label={t('close')}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Current Booking Info */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-medium text-slate-900 mb-3">{t('currentBookingInfo')}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">{t('guest')}:</span>
                  <div className="font-medium text-slate-900">{request.reqClientName}</div>
                </div>
                <div>
                  <span className="text-slate-600">{t('unit')}:</span>
                  <div className="font-medium text-slate-900">{request.unit?.unitName} - {request.unit?.unitCode}</div>
                </div>
                <div>
                  <span className="text-slate-600">{t('originalCheckout')}:</span>
                  <div className="font-medium text-slate-900">
                    {request.reqTo ? moment(request.reqTo).format('LL') : '-'} {request.reqTo ? moment.parseZone(request.reqTo).format('h:mm A') : ''}
                  </div>
                </div>
                <div>
                  <span className="text-slate-600">{t('currentTime')}:</span>
                  <div className="font-medium text-slate-900">{moment().format('L LT')}</div>
                </div>
                <div>
                  <span className="text-slate-600">{t('totalAmountLabel')}</span>
                  <div className="font-medium text-slate-900">{(request.reqTotalCost || 0).toLocaleString()} {t('EGP')}</div>
                </div>
                <div>
                  <span className="text-slate-600">{t('ratePerNightLabel')}</span>
                  <div className="font-medium text-slate-900">{nights > 0 ? ((request.reqTotalCost || 0) / (request.reqNights || nights)).toLocaleString() : '0'} {t('EGP')}</div>
                </div>
              </div>
            </div>

            {/* Requested Checkout Time */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('requestedCheckout')}
              </label>
              <input
                type="datetime-local"
                value={requestedCheckOut}
                onChange={(e) => setRequestedCheckOut(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Checkout Impact Display */}
            {checkoutDetails && (
              <div className={`p-4 rounded-xl border-2 ${checkoutDetails.type === 'early'
                ? 'bg-green-50 border-green-200'
                : checkoutDetails.type === 'late'
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-blue-50 border-blue-200'
                }`}>
                <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                  {checkoutDetails.type === 'early' ? (
                    <CheckCircle size={24} className="text-green-600" />
                  ) : checkoutDetails.type === 'late' ? (
                    <AlertTriangle size={24} className="text-orange-600" />
                  ) : (
                    <Clock size={24} className="text-blue-600" />
                  )}
                  <div>
                    <h4 className={`font-semibold ${checkoutDetails.type === 'early' ? 'text-green-900' : checkoutDetails.type === 'late' ? 'text-orange-900' : 'text-blue-900'
                      }`}>{checkoutDetails.type === 'early' ? t('earlyCheckout') : checkoutDetails.type === 'late' ? t('lateCheckout') : t('onTimeCheckout')}</h4>
                    <p className={`text-sm ${checkoutDetails.type === 'early'
                      ? 'text-green-700'
                      : checkoutDetails.type === 'late'
                        ? 'text-orange-700'
                        : 'text-blue-700'
                      }`}>
                      {checkoutDetails.hoursDifference.toFixed(1)} {t('hours')} {t(`${checkoutDetails.type}`)}
                    </p>
                  </div>
                </div>

                {/* EARLY CHECKOUT - REFUND SECTION */}
                {checkoutDetails.type === 'early' && (
                  <div className="space-y-4">
                    {/* Policy Calculation Breakdown */}
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <h5 className="font-medium text-green-900 mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                        <Calculator size={16} />
                        <span>{t('refundCalculationPolicy')}</span>
                      </h5>

                      <div className="space-y-2 text-sm text-slate-700">
                        <div className="flex justify-between">
                          <span>{t('unusedTime')}:</span>
                          <span className="font-medium">{checkoutDetails.hoursDifference.toFixed(1)} {t('hours')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('unusedTimeValue')}:</span>
                          <span className="font-medium">{(checkoutDetails.grossRefund || 0).toLocaleString()} {t('EGP')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('refundRate', { rate: policy.earlyCheckoutRules.refundPercentage })}:</span>
                          <span className="font-medium">{((checkoutDetails.grossRefund || 0) * (policy.earlyCheckoutRules.refundPercentage / 100)).toLocaleString()} {t('EGP')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('processingFee')}:</span>
                          <span className="font-medium text-red-600">-{(checkoutDetails.processingFee || 0).toLocaleString()} {t('EGP')}</span>
                        </div>
                        <div className="flex justify-between border-t border-green-300 pt-2 font-semibold text-green-800">
                          <span>{t('netRefundPolicy')}:</span>
                          <span>{Math.abs(checkoutDetails.additionalFees).toLocaleString()} {t('EGP')}</span>
                        </div>
                      </div>
                    </div>

                    {/* REFUND AMOUNT SECTION - EDITABLE */}
                    <div className="bg-green-100 border-2 border-green-300 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold text-green-900 flex items-center space-x-2 rtl:space-x-reverse">
                          <DollarSign size={18} />
                          <span>{t('finalRefundAmount')}</span>
                        </h5>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          {!isEditingRefund && (
                            <>
                              {/* <button
                                onClick={handlePrintLateCheckoutReceipt}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                                title={t('printFeeReceipt')}
                                disabled={finalFeeAmount <= 0}
                              >
                                <Printer size={16} />
                              </button> */}
                              <button
                                onClick={handlePrintRefundReceipt}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                                title={t('printRefundReceipt')}
                                disabled={finalRefundAmount <= 0}
                              >
                                <Printer size={16} />
                              </button>
                              <button
                                onClick={handleEditRefund}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                title={t('editRefundAmount')}
                              >
                                <Edit size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {isEditingRefund ? (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="flex-1">
                              <label className="block text-xs text-green-700 mb-1">
                                {t('customRefundAmount')}
                              </label>
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <input
                                  type="number"
                                  value={customRefundAmount || 0}
                                  onChange={(e) => setCustomRefundAmount(parseFloat(e.target.value) || 0)}
                                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                  step="0.01"
                                  min="0"
                                  max={checkoutDetails.grossRefund.toFixed(2) || 0}
                                />
                                <span className="text-sm text-green-700">{t('EGP')}</span>
                              </div>
                              <div className="text-xs text-green-600 mt-1">{t('maximum')}: {(checkoutDetails.grossRefund.toFixed(2) || 0).toLocaleString()} {t('EGP')}</div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={handleSaveRefund}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                                title={t('save')}
                              >
                                <Save size={16} />
                              </button>
                              <button
                                onClick={handleCancelRefund}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                title={t('cancel')}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Refund Percentage Display */}
                          {checkoutDetails.grossRefund && checkoutDetails.grossRefund > 0 && (
                            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                              <div className="flex justify-between text-sm">
                                <span className="text-green-700">{t('refundPercentage')}</span>
                                <span className="font-semibold text-green-800">
                                  {((customRefundAmount || 0) / checkoutDetails.grossRefund * 100).toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-bold text-green-700">
                              +{finalRefundAmount.toLocaleString()} {t('EGP')}
                            </span>
                            <div className="text-right text-sm text-green-600">
                              <div>{t('cashRefund')}</div>
                              <div className="text-xs">{new Date().toLocaleDateString()}</div>
                            </div>
                          </div>

                          {/* Custom Amount Notice */}
                          {customRefundAmount !== null && customRefundAmount !== Math.abs(checkoutDetails.additionalFees) && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-sm text-blue-700 font-medium">{t('refundAdjustedNotice')}</span>
                              </div>
                              <div className="mt-2 text-xs text-blue-600 space-y-1">
                                <div className="flex justify-between">
                                  <span>{t('originalAmountPolicy')}:</span>
                                  <span>{Math.abs(checkoutDetails.additionalFees).toLocaleString()} {t('EGP')}</span>
                                </div>
                                <div className="flex justify-between font-medium">
                                  <span>{t('customAmount')}:</span>
                                  <span>{finalRefundAmount.toLocaleString()} {t('EGP')}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Refund Details Summary */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <h6 className="font-medium text-green-900 mb-2 text-sm">
                              {t('refundSummary')}
                            </h6>
                            <div className="space-y-1 text-xs text-green-700">
                              <div className="flex justify-between">
                                <span>{t('refundType')}:</span>
                                <span className="font-medium">{t('earlyCheckout')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t('timeSaved')}:</span>
                                <span className="font-medium">{checkoutDetails.hoursDifference.toFixed(1)} {t('hours')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t('refundAmount')}:</span>
                                <span className="font-bold text-green-800">{finalRefundAmount.toLocaleString()} {t('EGP')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t('refundMethod')}:</span>
                                <span className="font-medium">{t('cash')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t('processingDate')}:</span>
                                <span className="font-medium">{new Date().toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* LATE CHECKOUT - FEE SECTION */}
                {checkoutDetails.type === 'late' && (
                  <div className="space-y-4">
                    {/* Policy Calculation */}
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <h5 className="font-medium text-orange-900 mb-3">{t('feeCalculationTitle')}</h5>
                      <div className="space-y-2 text-sm text-slate-700">
                        <div className="flex justify-between">
                          <span>{t('lateTime')}:</span>
                          <span className="font-medium">{checkoutDetails.hoursDifference.toFixed(1)} {t('hours')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('freeGracePeriodLabel')}:</span>
                          <span className="font-medium">{policy.lateCheckoutRules.gracePeriodMinutes} {t('minutes')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('hourlyRate')}:</span>
                          <span className="font-medium">{policy.lateCheckoutRules.hourlyRate} {t('EGP')}</span>
                        </div>
                        <div className="flex justify-between border-t border-orange-300 pt-2 font-semibold text-orange-800">
                          <span>{t('totalFeePolicy')}:</span>
                          <span>{checkoutDetails.additionalFees.toLocaleString()} {t('EGP')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Final Fee Amount - Editable */}
                    <div className="bg-orange-100 border-2 border-orange-300 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold text-orange-900">
                          {t('finalFeeAmount')}
                        </h5>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <button
                            onClick={handlePrintLateCheckoutReceipt}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title={t('printFeeReceipt')}
                            disabled={finalFeeAmount <= 0}
                          >
                            <Printer size={16} />
                          </button>
                          {!isEditingAmount && (
                            <button
                              onClick={handleAmountEdit}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title={t('editFeeAmount')}
                            >
                              <Edit size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      {isEditingRefund ? (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="flex-1">
                              <label className="block text-xs text-orange-700 mb-1">
                                {t('customFeeAmount')}
                              </label>
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <input
                                  type="number"
                                  value={customRefundAmount || 0}
                                  onChange={(e) => setCustomRefundAmount(parseFloat(e.target.value) || 0)}
                                  className="flex-1 px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                  step="0.01"
                                  min="0"
                                />
                                <span className="text-sm text-orange-700">{t('EGP')}</span>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={handleSaveRefund}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              >
                                <Save size={16} />
                              </button>
                              <button
                                onClick={handleCancelRefund}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-orange-700">
                              +{finalFeeAmount.toLocaleString()} {t('EGP')}
                            </span>
                          </div>
                          {/* Custom Amount Notice for Late Checkout */}
                          {customRefundAmount !== null && customRefundAmount !== checkoutDetails.additionalFees && (
                            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-sm text-blue-700 font-medium">
                                  {t('feeAmountManuallyAdjusted')}
                                </span>
                              </div>
                              <div className="mt-2 text-xs text-blue-600 space-y-1">
                                <div className="flex justify-between">
                                  <span>{t('originalAmountPolicy')}:</span>
                                  <span>{checkoutDetails.additionalFees.toLocaleString()} {t('EGP')}</span>
                                </div>
                                <div className="flex justify-between font-medium">
                                  <span>{t('customAmount')}:</span>
                                  <span>{finalFeeAmount.toLocaleString()} {t('EGP')}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ON-TIME CHECKOUT */}
                {checkoutDetails.type === 'onTime' && (
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <CheckCircle size={20} className="text-blue-600" />
                      <div>
                        <h5 className="font-medium text-blue-900">
                          {t('onTimeCheckout')}
                        </h5>
                        <p className="text-sm text-blue-700">
                          {t('noAdditionalFeesOrRefunds')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('reason')} ({t('optional')})
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder={t('reasonPlaceholder')}
              />
            </div>

            {/* Policy Information */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">
                {t('checkoutPolicyTitle')}
                <span className="text-xs text-blue-600 ml-2 rtl:mr-2 rtl:ml-0">
                  ({t('flexible')})
                </span>
              </h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• {t('earlyCheckoutPolicyLine', { rate: policy.earlyCheckoutRules?.refundPercentage })}</p>
                <p>• {t('lateCheckoutPolicyLine', { hourlyRate: policy.lateCheckoutRules?.hourlyRate, gracePeriodMinutes: policy.lateCheckoutRules?.gracePeriodMinutes })}</p>
                <p>• {t('processingFeePolicyLine', { processingFee: policy.earlyCheckoutRules?.processingFee })}</p>
                <p className="text-blue-600 font-medium">• {t('agentCanAdjust')}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 rounded-b-2xl">
            <div className="flex space-x-3 rtl:space-x-reverse">
              <button
                onClick={handleSubmit}
                disabled={!requestedCheckOut || !checkoutDetails || isSubmitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200"
              >
                {isSubmitting ? t('submitting') : t('submitRequest')}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <X size={20} />
                <span>{t('cancel')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Refund Receipt Modal */}
      {refundForPrint && (
        <PrintReceiptModal
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setRefundForPrint(null);
          }}
          type="refund"
          request={getBookingRequestForPrint()}
          refund={refundForPrint}
          t={t}
        />
      )}

      {/* Print Late Checkout Fee Receipt Modal */}
      {showPrintModal && checkoutDetails?.type === 'late' && (
        <PrintReceiptModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          type="late-checkout"
          request={request}
          lateCheckout={{
            feeAmount: Math.abs(finalFeeAmount),
            hoursLate: checkoutDetails.hoursDifference,
            reason: reason.trim() || undefined,
            hourlyRate: policy.lateCheckoutRules?.hourlyRate || 100,
            gracePeriodMinutes: policy.lateCheckoutRules?.gracePeriodMinutes || 30
          }}
        />
      )}
    </>
  );
};